import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from 'store';
import {
	designCreateAccepted, syncCreateStatus, designCreateFailed, resetDesignCreate,
} from 'store/slices/DesignCreateSlice';
import { fetchRoofData } from 'store/slices/RoofDataSlice/roofDataSlice';
import { setEnergyDerate } from 'store/slices/QueryParamsSlice';
import { syncCreateShadingState, shadingFailed } from 'store/slices/DsmShadingSlice';
import { getCreateStatus, CreateStatusSnap } from 'pages/LocationMap/api';
import { PARENT_EVENTS, API_ERROR_CODES, SHADING_STATUS } from 'constants/index';
import { TOOL_ERROR_CODES } from 'constants/toolError';
import { CustomError } from 'store/api/types';
import { postToolError } from 'parentEvents';
import { delayFromSchedule } from 'utils/pollSchedule';
import {
	markCreateDesignComplete, markFetchRoofDesignStart, markFetchRoofDesignComplete, resetDesignPipelineTiming,
	markQueueSnapshot, markShadingStart
} from 'utils/designLoadTiming';
import { decideDerateBasedUponMode } from '../../util';

// A build cannot finish inside ~10s, so wait that out before asking: 7s, 4s, then 3s repeating.
// NOTE: this also delays the FIRST status read on the 409 handoff below, where the build may already
// be nearly done — that path may want its own shorter schedule.
const CREATE_POLL_SCHEDULE_MS = [15000, 5000, 1000];
// Safety net (FE contract §2/§7): never poll a wedged build forever — give up after ~5 min and treat
// as terminal. Covers a stuck PENDING/PROCESSING and a FAILED snap missing retryable.
const SAFETY_TIMEOUT_MS = 10 * 60 * 1000;

// NOTE: created UNTYPED on purpose — same reasoning as dsmShadingListener (parametrising with
// <RootState, AppDispatch> makes the middleware type depend on the store type, collapsing
// RootState to `any` app-wide). getState/dispatch are cast inside the effect instead.
export const designCreateListener = createListenerMiddleware();

/**
 * The async design-create lifecycle, owned entirely by the store (no component host).
 * Started by `designCreateAccepted` (dispatched after the 202 POST). Polls create-status until
 * terminal, then:
 *   READY  → apply mode-based derate, refetch the design (fetchRoofData), notify the parent.
 *   FAILED → surface the errorCode to the parent (only once retries are exhausted).
 * The LocationMap component reads slice status for the loader and navigation.
 */
designCreateListener.startListening({
	actionCreator: designCreateAccepted,
	effect: async (action, api) => {
		api.cancelActiveListeners(); // only the newest create-watch runs at a time

		const getState = () => api.getState() as RootState;
		const dispatch = api.dispatch as AppDispatch;

		const { designUUID: uuid, designType, panelKey } = action.payload;
		if (!uuid) return;

		const handleReady = (mode: DesignModes | null) => {
			markCreateDesignComplete(); // the build is done — end of the create phase (no-op if unarmed)
			// mode drives the derate; the poll carries it on READY. Must be set before fetchRoofData.
			if (mode) {
				const { energyDerateRate, stateCode } = getState().QueryParams.data;
				const modeEnergyRate = decideDerateBasedUponMode(mode, energyDerateRate, stateCode ?? null);
				dispatch(setEnergyDerate({ energyDerate: modeEnergyRate }));
			}
			window.parent.postMessage({ id: PARENT_EVENTS.SET_HOUSE, detail: { designUUID: uuid } }, '*');
			// design-by-uuid is fetched here, only AFTER READY → the E_DESIGN_NOT_READY (409) race can't happen.
			dispatch(fetchRoofData({ uuid, panelKey, designType }));
			// flip to READY last → the component navigates only after the refetch is under way.
			dispatch(syncCreateStatus({ designUUID: uuid, status: 'READY', errorCode: null }));
		};

		const syncShading = (shading: CreateStatusSnap['shading']) => {
			if (!shading) return;
			dispatch(syncCreateShadingState(shading));

			if (shading.shadingStatus === SHADING_STATUS.QUEUED || shading.shadingStatus === SHADING_STATUS.RUNNING) {
				markShadingStart({ status: shading.shadingStatus });
			}
			if (shading.shadingStatus === SHADING_STATUS.FAILED) {
				dispatch(shadingFailed({ message: 'Shading recompute failed' }));
			}
		};

		const handleFailure = (errorCode: string | null) => {
			resetDesignPipelineTiming(); // the pipeline died here — no design fetch or shading to time
			console.log('errorCode', errorCode);
			dispatch(designCreateFailed({ errorCode }));
			// Parent-compat: the parent listens for UNSUPPORTED_ADDRESS to reset its create UI on ANY
			// terminal create failure (mirrors the old component onError), not only the address codes.
			window.parent.postMessage({ id: 'SOLARQUOTEEMBED_UNSUPPORTED_ADDRESS' }, '*');
			postToolError(
				TOOL_ERROR_CODES.CREATE_DESIGN_FAILED,
				new Error(`Design creation failed${errorCode ? `: ${errorCode}` : ''}`),
			);
		};

		// Poll create-status until terminal. `api.delay` is cancellation-aware, so resetDesignCreate
		// (or a newer create) tears this loop down cleanly.
		let polls = 0;
		const startedAt = Date.now();
		// eslint-disable-next-line no-constant-condition
		while (true) {
			// The wait comes BEFORE each read — a status read fired the instant the 202 landed can only
			// ever say PENDING. A failed read consumes its slot too, so a retry uses the next delay.
			await api.delay(delayFromSchedule(polls++, CREATE_POLL_SCHEDULE_MS));

			// Safety net: bound the watch so a never-terminal build can't poll indefinitely. Wall-clock,
			// not a poll count — the two stop being interchangeable once the cadence varies.
			if (Date.now() - startedAt >= SAFETY_TIMEOUT_MS) return handleFailure('E_CREATE_TIMEOUT');

			let snap: CreateStatusSnap;
			try {
				snap = await getCreateStatus(uuid);
			} catch {
				continue; // transient network/API error — the top-of-loop delay is the wait before retrying
			}

			console.log('designStatus', snap);
			if (snap.metadata) markQueueSnapshot(snap.metadata);
			syncShading(snap.shading);

			if (snap.status === 'READY') return handleReady(snap.mode ?? null);
			// Give up when the build won't be retried (retryable === false) → fire immediately; or when a
			// retryable failure has burned through 3 build attempts. A retryable FAILED under that cap
			// means a retry is queued, so we keep waiting.
			if (snap.status === 'FAILED' && (snap.retryable === false || (snap.attempts ?? 0) >= 3)) {
				return handleFailure(snap.errorCode ?? null);
			}

			// PENDING / PROCESSING / retryable FAILED (under the attempt cap) → keep the loader up ("creating…").
			dispatch(syncCreateStatus({
				designUUID: uuid,
				status: snap.status === 'FAILED' ? 'PROCESSING' : snap.status,
				errorCode: null,
			}));
		}
	},
});

designCreateListener.startListening({
	actionCreator: resetDesignCreate,
	effect: (_a, api) => api.cancelActiveListeners(),
});

/**
 * Dev observability: stamp the start/complete of the fetch-roof-design phase of the
 * create → fetch → shading timer. No-op unless a create-design POST armed it (utils/designLoadTiming).
 */
designCreateListener.startListening({
	matcher: fetchRoofData.pending.match,
	effect: () => markFetchRoofDesignStart(),
});

designCreateListener.startListening({
	matcher: fetchRoofData.fulfilled.match,
	effect: () => markFetchRoofDesignComplete(),
});

/**
 * Direct-load race (FE contract §3/§5): the Home page (or any caller) can request design-by-uuid
 * while the background build is still running → BE replies 409 E_DESIGN_NOT_READY. That is "not an
 * error — keep polling", so hand the id off to the create-watch above instead of surfacing it.
 */
designCreateListener.startListening({
	matcher: fetchRoofData.rejected.match,
	effect: (action, api) => {
		const getState = () => api.getState() as RootState;
		const dispatch = api.dispatch as AppDispatch;

		const payload = action.payload as CustomError | undefined;
		if (payload?.code !== 409 || payload?.error !== API_ERROR_CODES.DESIGN_NOT_READY) return;
		// Don't restart a watch that's already running (e.g. the post-READY refetch losing a race).
		if (getState().designCreate.data.status !== 'IDLE') return;

		const { uuid, panelKey, designType } = action.meta.arg;
		if (!uuid) return;
		dispatch(designCreateAccepted({ designUUID: uuid, designType, panelKey }));
	},
});
